import { getParentGraphStep } from '../getParentGraphStep.js';

// An If/Else `branches` array is a POSITIONAL, NULLABLE list of AT MOST two
// lane heads: [thenHead, elseHead]. An "available" slot is one that is genuinely
// unfilled — null or missing ONLY. A falsy-but-present value (e.g. '' or false)
// is an occupied lane, not a free slot, and must never be overwritten.
const isEmptyBranchSlot = (branch) => branch === null || branch === undefined;

// The two legal positional lanes of an If/Else: index 0 = Then, index 1 = Else.
const IF_ELSE_BRANCH_SLOTS = [0, 1];

// Decide how a pasted block attaches to its target parent: as a branch lane, as a
// child, or (default) on the parent's exit. When the copied block originated from a
// branch and the target is an ifElse, the paste fills a free positional lane (0/1
// only); if both lanes are occupied it returns the exit placement explicitly. A
// copied block containing an over-long (> 2 lane) If/Else is landed on the exit
// regardless of copied order (never branch-placed, never normalized).
export const determineTargetRelation = (parentStepId, parentStep, targetSteps, copiedSteps, sourceGraph) => {
  // Find the parent step data to understand its capabilities
  const parentStepData = targetSteps.find((step) => step.id === parentStepId);

  if (!parentStepData) {
    return { relation: 'exit', index: null };
  }

  // Check if parent can have branches (like ifElse steps)
  const canHaveBranches = parentStepData.appSlug === 'ifElse';

  // Check if parent can have children (like iterate steps)
  const canHaveChildren = ['iterate'].includes(parentStepData.appSlug) || parentStepData.options?.pagination?.type === 'batch';

  // If we have copied steps, try to determine their original relationship
  if (copiedSteps && sourceGraph) {
    // Malformed source block (ORDER-INDEPENDENT): if the copied source graph
    // contains ANY If/Else node with an OVERLONG (> 2) `branches` array, that is
    // already-invalid data. Do NOT branch-place the block and do NOT normalize the
    // source — land the whole block on the target's exit, regardless of the copied
    // selection order (descendant-first vs parent-first). The pasted node keeps its
    // 3+ lanes verbatim and the Phase-3 validation cap rejects it downstream. This
    // runs BEFORE the order-dependent relationship inference so a parent-first paste
    // (whose copiedSteps[0] is the If/Else root, not a branch child) is covered too.
    //
    // A NON-array `branches` is not handled here: the shared copy machinery
    // normalizes it to `[]` at getWorkflowGraphSelection.js:12 (a pre-existing
    // behavior, common to all copy/paste — tracked in GRA-18596), so it never
    // reaches here as an over-long array.
    const sourceHasOverlongBranches = Object.values(sourceGraph).some((sourceNode) => Array.isArray(sourceNode?.branches) && sourceNode.branches.length > 2);
    if (sourceHasOverlongBranches) {
      return { relation: 'exit', index: null };
    }

    const firstCopiedStep = copiedSteps[0];
    if (firstCopiedStep) {
      const originalParent = getParentGraphStep(firstCopiedStep.id, sourceGraph);

      // If the original steps were in a branch relationship and the target can have branches
      if (originalParent?.relation === 'branch' && canHaveBranches) {
        // Find the first available branch slot, bounded to the two legal lanes.
        const currentBranches = parentStep.branches || [];
        const firstEmptyBranchIndex = IF_ELSE_BRANCH_SLOTS.find((index) => isEmptyBranchSlot(currentBranches[index]));
        if (firstEmptyBranchIndex !== undefined) {
          return { relation: 'branch', index: firstEmptyBranchIndex };
        }
        // Both legal lanes are occupied: do NOT append a third lane, and do NOT
        // fall through to the default logic — a batch-pagination If/Else has
        // canHaveChildren=true, so falling through would wrongly child-place the
        // paste. Land it on the exit explicitly.
        return { relation: 'exit', index: null };
      }

      // If the original steps were children and the target can have children
      if (originalParent?.relation === 'child' && canHaveChildren && !parentStep.child) {
        return { relation: 'child', index: null };
      }
    }
  }

  // Default fallback logic based on parent capabilities
  if (canHaveBranches && (!parentStep.branches || parentStep.branches.every(isEmptyBranchSlot))) {
    // If parent can have branches and has no filled lane yet, use first branch
    return { relation: 'branch', index: 0 };
  }

  if (canHaveChildren && !parentStep.child) {
    // If parent can have children and has no child yet, use child relationship
    return { relation: 'child', index: null };
  }

  // Default to exit relationship
  return { relation: 'exit', index: null };
};
